import React, { useState, useRef } from 'react';
import whiteLabelService from '../../services/whiteLabelService';
import useAutoSave from '../../hooks/useAutoSave';
import AutoSaveStatus from '../Global/AutoSaveStatus';
import './scss/BrandingConfiguration.scss';

// Simple preview component that shows preloader preview
const PreloaderPreview = ({ branding }) => {
  const preloaderConfig = branding.preloader || {};
  const {
    type = 'default',
    customLogo,
    showFromText = true,
    backgroundColor,
    logoSize = 'medium',
    animationType = 'fade'
  } = preloaderConfig;

  const appName = branding.appName || 'Your App';
  const hideZoobbe = branding.hideZoobbeBranding || false;

  const preloaderStyle = backgroundColor ? { backgroundColor } : {};

  const renderPreloaderContent = () => {
    switch (type) {
      case 'minimal':
        return (
          <div className="preview-preloader-content minimal">
            <div className={`preview-preloader-logo ${logoSize} ${animationType}`}>
              {customLogo ? (
                <img src={customLogo} alt={appName} className="custom-preloader-logo" />
              ) : (
                <div className="minimal-spinner"></div>
              )}
            </div>
          </div>
        );

      case 'logo-only':
        return (
          <div className="preview-preloader-content logo-only">
            <div className={`preview-preloader-logo ${logoSize} ${animationType}`}>
              {customLogo ? (
                <img src={customLogo} alt={appName} className="custom-preloader-logo" />
              ) : branding.logo ? (
                <img src={branding.logo} alt={appName} className="custom-preloader-logo" />
              ) : (
                <div className="default-logo-placeholder">Logo</div>
              )}
            </div>
          </div>
        );

      case 'text-only':
        return (
          <div className="preview-preloader-content text-only">
            <div className={`preview-preloader-text ${animationType}`}>
              <span className="app-name">{appName}</span>
            </div>
          </div>
        );

      case 'custom':
        return (
          <div className="preview-preloader-content custom">
            <div className={`preview-preloader-logo ${logoSize} ${animationType}`}>
              {customLogo ? (
                <img src={customLogo} alt={appName} className="custom-preloader-logo" />
              ) : branding.logo ? (
                <img src={branding.logo} alt={appName} className="custom-preloader-logo" />
              ) : (
                <div className="default-logo-placeholder">Logo</div>
              )}
            </div>
            {showFromText && !hideZoobbe && (
              <div className="preview-preloader-from">
                <span>from</span>
                <span className="preview-app-text">{appName}</span>
              </div>
            )}
          </div>
        );

      default: // 'default' type
        return (
          <div className="preview-preloader-content">
            <div className={`preview-preloader-logo ${logoSize} ${animationType}`}>
              {customLogo ? (
                <img src={customLogo} alt={appName} className="custom-preloader-logo" />
              ) : branding.logo ? (
                <img src={branding.logo} alt={appName} className="custom-preloader-logo" />
              ) : (
                <div className="default-logo-placeholder">Logo</div>
              )}
            </div>
            {showFromText && (
              <div className="preview-preloader-from">
                <span>from</span>
                <span className="preview-app-text">{hideZoobbe ? appName : 'Zoobbe'}</span>
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div
      className={`preview-preloader ${type} ${animationType}`}
      style={preloaderStyle}
    >
      {renderPreloaderContent()}
    </div>
  );
};

const BrandingConfiguration = ({ config, onUpdate, loading }) => {
  const [branding, setBranding] = useState(config?.branding || {});
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoDarkUploading, setLogoDarkUploading] = useState(false);
  const [logoIconUploading, setLogoIconUploading] = useState(false);
  const [faviconUploading, setFaviconUploading] = useState(false);

  const fileInputRef = useRef(null);
  const fileDarkInputRef = useRef(null);
  const logoIconInputRef = useRef(null);

  // Validation function
  const validateBranding = (data) => {
    const errors = {};
    const brandingData = data.branding || data;

    if (brandingData.appName) {
      const appNameValidation = whiteLabelService.validateAppName(brandingData.appName);
      if (!appNameValidation.valid) {
        errors.appName = appNameValidation.error;
      }
    }

    if (brandingData.primaryColor) {
      const colorValidation = whiteLabelService.validateColor(brandingData.primaryColor);
      if (!colorValidation.valid) {
        errors.primaryColor = colorValidation.error;
      }
    }

    if (brandingData.secondaryColor) {
      const colorValidation = whiteLabelService.validateColor(brandingData.secondaryColor);
      if (!colorValidation.valid) {
        errors.secondaryColor = colorValidation.error;
      }
    }

    if (brandingData.accentColor) {
      const colorValidation = whiteLabelService.validateColor(brandingData.accentColor);
      if (!colorValidation.valid) {
        errors.accentColor = colorValidation.error;
      }
    }

    if (brandingData.supportEmail) {
      const emailValidation = whiteLabelService.validateEmail(brandingData.supportEmail);
      if (!emailValidation.valid) {
        errors.supportEmail = emailValidation.error;
      }
    }

    if (brandingData.customTermsUrl && brandingData.customTermsUrl && !isValidUrl(brandingData.customTermsUrl)) {
      errors.customTermsUrl = 'Please enter a valid URL';
    }

    if (brandingData.customPrivacyUrl && brandingData.customPrivacyUrl && !isValidUrl(brandingData.customPrivacyUrl)) {
      errors.customPrivacyUrl = 'Please enter a valid URL';
    }

    return errors;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Auto-save hook
  const { autoSaving, lastSaved, errors, autoSave, clearFieldError, setFieldErrors } = useAutoSave(
    (data) => onUpdate(data),
    validateBranding
  );

  // Preloader type options for ZoobbeSelect
  const preloaderTypeOptions = [
    { value: 'default', label: 'Default - Logo with "from" text' },
    { value: 'minimal', label: 'Minimal - Simple spinner or logo' },
    { value: 'logo-only', label: 'Logo Only - Just the logo/brand mark' },
    { value: 'text-only', label: 'Text Only - App name as text' },
    { value: 'custom', label: 'Custom - Full customization' }
  ];

  // Logo size options
  const logoSizeOptions = [
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' },
    { value: 'large', label: 'Large' }
  ];

  // Animation type options
  const animationTypeOptions = [
    { value: 'fade', label: 'Fade - Gentle fade in/out' },
    { value: 'pulse', label: 'Pulse - Scaling pulse animation' },
    { value: 'spin', label: 'Spin - Rotating animation' },
    { value: 'bounce', label: 'Bounce - Bouncing motion' }
  ];



  const handleInputChange = (field, value) => {
    const updatedBranding = {
      ...branding,
      [field]: value
    };

    setBranding(updatedBranding);

    // Clear error for this field
    clearFieldError(field);

    // Auto-save the changes
    autoSave({ branding: updatedBranding });
  };

  const handlePreloaderInputChange = (field, value) => {
    const updatedBranding = {
      ...branding,
      preloader: {
        ...branding.preloader,
        [field]: value
      }
    };

    setBranding(updatedBranding);

    // Clear error for this field
    clearFieldError(`preloader.${field}`);

    // Auto-save the changes
    autoSave({ branding: updatedBranding });
  };


  // SEO field updater for nested branding.seo
  const handleSeoChange = (field, value) => {
    const updatedBranding = {
      ...branding,
      seo: {
        ...(branding.seo || {}),
        [field]: value
      }
    };

    setBranding(updatedBranding);

    // Clear error for this field path (if any validation added later)
    clearFieldError(`seo.${field}`);

    // Auto-save the changes
    autoSave({ branding: updatedBranding });
  };



  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      setFieldErrors({ logo: 'Please select an image file' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setFieldErrors({ logo: 'File size must be less than 5MB' });
      return;
    }

    try {
      setLogoUploading(true);

      const response = await whiteLabelService.uploadLogo(file);

      // Update local state immediately for UI feedback
      const updatedBranding = {
        ...branding,
        logo: response.logoUrl
      };
      setBranding(updatedBranding);

      // Save to backend immediately
      await onUpdate({ branding: updatedBranding });

      clearFieldError('logo');
    } catch (error) {
      setFieldErrors({ logo: error.message });
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoDarkUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      setFieldErrors({ logoDark: 'Please select an image file' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setFieldErrors({ logoDark: 'File size must be less than 5MB' });
      return;
    }

    try {
      setLogoDarkUploading(true);

      const response = await whiteLabelService.uploadLogoDark(file);

      // Update local state immediately for UI feedback
      const updatedBranding = {
        ...branding,
        logoDark: response.logoDarkUrl
      };
      setBranding(updatedBranding);

      // Save to backend immediately
      await onUpdate({ branding: updatedBranding });

      clearFieldError('logoDark');
    } catch (error) {
      setFieldErrors({ logoDark: error.message });
    } finally {
      setLogoDarkUploading(false);
    }
  };

  const handleLogoIconUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      setFieldErrors({ logoIcon: 'Please select an image file' });
      return;
    }

    if (file.size > 2 * 1024 * 1024) { // 2MB limit
      setFieldErrors({ logoIcon: 'File size must be less than 2MB' });
      return;
    }

    try {
      setLogoIconUploading(true);

      const response = await whiteLabelService.uploadLogoIcon(file);

      // Update local state immediately for UI feedback
      const updatedBranding = {
        ...branding,
        logoIcon: response.logoIconUrl
      };
      setBranding(updatedBranding);

      // Save to backend immediately
      await onUpdate({ branding: updatedBranding });

      clearFieldError('logoIcon');
    } catch (error) {
      setFieldErrors({ logoIcon: error.message });
    } finally {
      setLogoIconUploading(false);
    }
  };

  const handleFaviconUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.ico,.png,.jpg,.jpeg';
    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file
      if (!file.type.startsWith('image/') && !file.type.includes('icon')) {
        setFieldErrors({ favicon: 'Please select an image or icon file' });
        return;
      }

      if (file.size > 2 * 1024 * 1024) { // 2MB limit for favicon
        setFieldErrors({ favicon: 'File size must be less than 2MB' });
        return;
      }

      try {
        setFaviconUploading(true);
        const response = await whiteLabelService.uploadFavicon(file);

        // Update local state immediately for UI feedback
        const updatedBranding = {
          ...branding,
          favicon: response.faviconUrl
        };
        setBranding(updatedBranding);

        // Save to backend immediately
        await onUpdate({ branding: updatedBranding });

        clearFieldError('favicon');
      } catch (error) {
        setFieldErrors({ favicon: error.message });
      } finally {
        setFaviconUploading(false);
      }
    };
    input.click();
  };



  const handleLogoReset = async () => {
    try {
      const updatedBranding = {
        ...branding,
        logo: null
      };
      setBranding(updatedBranding);

      // Save to backend immediately
      await onUpdate({ branding: updatedBranding });

      clearFieldError('logo');
    } catch (error) {
      setFieldErrors({ logo: error.message });
    }
  };

  const handleLogoDarkReset = async () => {
    try {
      const updatedBranding = {
        ...branding,
        logoDark: null
      };
      setBranding(updatedBranding);

      // Save to backend immediately
      await onUpdate({ branding: updatedBranding });

      clearFieldError('logoDark');
    } catch (error) {
      setFieldErrors({ logoDark: error.message });
    }
  };

  const handleLogoIconReset = async () => {
    try {
      const updatedBranding = {
        ...branding,
        logoIcon: null
      };
      setBranding(updatedBranding);

      // Save to backend immediately
      await onUpdate({ branding: updatedBranding });

      clearFieldError('logoIcon');
    } catch (error) {
      setFieldErrors({ logoIcon: error.message });
    }
  };

  const handleFaviconReset = async () => {
    try {
      const updatedBranding = {
        ...branding,
        favicon: null
      };
      setBranding(updatedBranding);

      // Save to backend immediately
      await onUpdate({ branding: updatedBranding });

      clearFieldError('favicon');
    } catch (error) {
      setFieldErrors({ favicon: error.message });
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm('Are you sure you want to reset all branding settings to default? This action cannot be undone.')) {
      return;
    }

    try {
      const defaultBranding = {
        appName: 'Zoobbe',
        logo: null,
        favicon: null,
        primaryColor: '#0966ff',
        secondaryColor: null,
        accentColor: null,
        fontFamily: null,
        customCSS: null,
        hideZoobbeBranding: true,
        customFooter: null,
        supportEmail: null,
        customTermsUrl: null,
        customPrivacyUrl: null
      };

      setBranding(defaultBranding);

      // Save to backend immediately
      await onUpdate({ branding: defaultBranding });

      setFieldErrors({});
    } catch (error) {
      setFieldErrors({ general: error.message });
    }
  };



  return (
    <div className="branding-configuration">
      <div className="section">
        <h3>App Identity</h3>

        <div className="form-group">
          <label>App Name</label>
          <input
            type="text"
            value={branding.appName || ''}
            onChange={(e) => handleInputChange('appName', e.target.value)}
            placeholder="Your App Name"
            className={errors.appName ? 'error' : ''}
          />
          {errors.appName && <span className="error-text">{errors.appName}</span>}
          <small>This will replace "Zoobbe" throughout the application</small>
        </div>

        <div className="logos-grid">
          <div className="form-group">
            <label>Full Logo - Light Theme (Horizontal)</label>
            <div className="logo-upload">
              {branding.logo ? (
                <div className="logo-preview">
                  <img src={branding.logo} alt="Logo" />
                  <div className="logo-actions">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={logoUploading}
                      className="btn btn-secondary"
                    >
                      {logoUploading ? 'Uploading...' : 'Change'}
                    </button>
                    <button
                      type="button"
                      onClick={handleLogoReset}
                      disabled={logoUploading}
                      className="btn btn-outline"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div className="logo-placeholder">
                  <span className="material-symbols-outlined">image</span>
                  <p>No logo uploaded</p>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={logoUploading}
                  >
                    {logoUploading ? 'Uploading...' : 'Upload Logo'}
                  </button>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                style={{ display: 'none' }}
              />
            </div>
            {errors.logo && <span className="error-text">{errors.logo}</span>}
            <small>For light theme. Recommended: 200x60px, max 5MB</small>
          </div>

          <div className="form-group">
            <label>Full Logo - Dark Theme (Horizontal)</label>
            <div className="logo-upload">
              {branding.logoDark ? (
                <div className="logo-preview">
                  <img src={branding.logoDark} alt="Dark Logo" />
                  <div className="logo-actions">
                    <button
                      type="button"
                      onClick={() => fileDarkInputRef.current?.click()}
                      disabled={logoDarkUploading}
                      className="btn btn-secondary"
                    >
                      {logoDarkUploading ? 'Uploading...' : 'Change'}
                    </button>
                    <button
                      type="button"
                      onClick={handleLogoDarkReset}
                      disabled={logoDarkUploading}
                      className="btn btn-outline"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div className="logo-placeholder">
                  <span className="material-symbols-outlined">image</span>
                  <p>No dark logo uploaded</p>
                  <button
                    type="button"
                    onClick={() => fileDarkInputRef.current?.click()}
                    disabled={logoDarkUploading}
                  >
                    {logoDarkUploading ? 'Uploading...' : 'Upload Dark Logo'}
                  </button>
                </div>
              )}

              <input
                ref={fileDarkInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoDarkUpload}
                style={{ display: 'none' }}
              />
            </div>
            {errors.logoDark && <span className="error-text">{errors.logoDark}</span>}
            <small>For dark theme. Recommended: 200x60px, max 5MB</small>
          </div>

          <div className="form-group">
            <label>Logo Icon (Square)</label>
            <div className="logo-upload">
              {branding.logoIcon ? (
                <div className="logo-preview logo-icon-preview">
                  <img src={branding.logoIcon} alt="Logo Icon" />
                  <div className="logo-actions">
                    <button
                      type="button"
                      onClick={() => logoIconInputRef.current?.click()}
                      disabled={logoIconUploading}
                      className="btn btn-secondary"
                    >
                      {logoIconUploading ? 'Uploading...' : 'Change'}
                    </button>
                    <button
                      type="button"
                      onClick={handleLogoIconReset}
                      disabled={logoIconUploading}
                      className="btn btn-outline"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div className="logo-placeholder">
                  <span className="material-symbols-outlined">image</span>
                  <p>No icon uploaded</p>
                  <button
                    type="button"
                    onClick={() => logoIconInputRef.current?.click()}
                    disabled={logoIconUploading}
                  >
                    {logoIconUploading ? 'Uploading...' : 'Upload Icon'}
                  </button>
                </div>
              )}

              <input
                ref={logoIconInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoIconUpload}
                style={{ display: 'none' }}
              />
            </div>
            {errors.logoIcon && <span className="error-text">{errors.logoIcon}</span>}
            <small>For collapsed sidebar. Recommended: 64x64px square, max 2MB</small>
          </div>
        </div>

        <div className="form-group">
          <label>Favicon (Optional)</label>
          <div className="favicon-upload">
            {branding.favicon ? (
              <div className="favicon-preview">
                <img src={branding.favicon} alt="Favicon" />
                <div className="favicon-actions">
                  <button
                    type="button"
                    onClick={() => handleFaviconUpload()}
                    disabled={faviconUploading}
                    className="btn btn-secondary"
                  >
                    {faviconUploading ? 'Uploading...' : 'Change Favicon'}
                  </button>
                  <button
                    type="button"
                    onClick={handleFaviconReset}
                    disabled={faviconUploading}
                    className="btn btn-outline"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div className="favicon-placeholder">
                <span className="material-symbols-outlined">web</span>
                <p>No favicon uploaded</p>
                <button
                  type="button"
                  onClick={() => handleFaviconUpload()}
                  disabled={faviconUploading}
                >
                  {faviconUploading ? 'Uploading...' : 'Upload Favicon'}
                </button>
              </div>
            )}
          </div>
          <small>Recommended size: 32x32px or 16x16px, .ico or .png format</small>
        </div>
      </div>

      <div className="section">
        <h3>Colors</h3>

        <div className="color-grid">
          <div className="form-group">
            <label>Primary Color</label>
            <div className="color-input">
              <input
                type="color"
                value={branding.primaryColor || '#0966ff'}
                onChange={(e) => handleInputChange('primaryColor', e.target.value)}
              />
              <input
                type="text"
                value={branding.primaryColor || '#0966ff'}
                onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                placeholder="#0966ff"
                className={errors.primaryColor ? 'error' : ''}
              />
            </div>
            {errors.primaryColor && <span className="error-text">{errors.primaryColor}</span>}
          </div>

          <div className="form-group">
            <label>Secondary Color (Optional)</label>
            <div className="color-input">
              <input
                type="color"
                value={branding.secondaryColor || '#38a801'}
                onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
              />
              <input
                type="text"
                value={branding.secondaryColor || ''}
                onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                placeholder="#38a801"
                className={errors.secondaryColor ? 'error' : ''}
              />
            </div>
            {errors.secondaryColor && <span className="error-text">{errors.secondaryColor}</span>}
          </div>

          <div className="form-group">
            <label>Accent Color (Optional)</label>
            <div className="color-input">
              <input
                type="color"
                value={branding.accentColor || '#ff6b35'}
                onChange={(e) => handleInputChange('accentColor', e.target.value)}
              />
              <input
                type="text"
                value={branding.accentColor || ''}
                onChange={(e) => handleInputChange('accentColor', e.target.value)}
                placeholder="#ff6b35"
                className={errors.accentColor ? 'error' : ''}
              />
            </div>
            {errors.accentColor && <span className="error-text">{errors.accentColor}</span>}
            <small>Used for highlights and call-to-action elements</small>
          </div>
        </div>
      </div>

      <div className="section">
        <h3>Typography</h3>

        <div className="form-group">
          <label>Font Family (Optional)</label>
          <select
            value={branding.fontFamily || ''}
            onChange={(e) => handleInputChange('fontFamily', e.target.value)}
          >
            <option value="">Default Font</option>
            <option value="Inter, sans-serif">Inter</option>
            <option value="Roboto, sans-serif">Roboto</option>
            <option value="Open Sans, sans-serif">Open Sans</option>
            <option value="Lato, sans-serif">Lato</option>
            <option value="Montserrat, sans-serif">Montserrat</option>
            <option value="Poppins, sans-serif">Poppins</option>
          </select>
          <small>Choose a font family for your branded interface</small>
        </div>
      </div>


      {/* <div className="section">
        <h3>Preloader Settings</h3>

        <div className="preloader-settings-container">
          <div className="preloader-config">
            <div className="form-group">
              <div className="toggle-group">
                <label>Enable Preloader</label>
                <Toggle
                  checked={branding.preloader?.enabled !== false}
                  onChange={(enabled) => handlePreloaderInputChange('enabled', enabled)}
                  label=""
                  disabled={loading}
                />
              </div>
              <small>Show a loading screen when the application starts</small>
            </div>

            {branding.preloader?.enabled !== false && (
              <>
                <div className="form-group">
                  <label>Preloader Type</label>
                  <ZoobbeSelect
                    options={preloaderTypeOptions}
                    defaultSelectedOption={preloaderTypeOptions.findIndex(opt => opt.value === (branding.preloader?.type || 'default'))}
                    onSelect={(option) => handlePreloaderInputChange('type', option.value)}
                    className="preloader-select"
                  />
                  <small>Choose the style of preloader to display</small>
                </div>

                <div className="form-group">
                  <label>Logo Size</label>
                  <ZoobbeSelect
                    options={logoSizeOptions}
                    defaultSelectedOption={logoSizeOptions.findIndex(opt => opt.value === (branding.preloader?.logoSize || 'medium'))}
                    onSelect={(option) => handlePreloaderInputChange('logoSize', option.value)}
                    className="preloader-select"
                  />
                  <small>Size of the logo in the preloader</small>
                </div>

                <div className="form-group">
                  <label>Animation Type</label>
                  <ZoobbeSelect
                    options={animationTypeOptions}
                    defaultSelectedOption={animationTypeOptions.findIndex(opt => opt.value === (branding.preloader?.animationType || 'fade'))}
                    onSelect={(option) => handlePreloaderInputChange('animationType', option.value)}
                    className="preloader-select"
                  />
                  <small>Animation effect for the preloader</small>
                </div>

                <div className="form-group">
                  <label>Custom Preloader Logo (Optional)</label>
                  <div className="file-upload-section">
                    {branding.preloader?.customLogo ? (
                      <div className="current-file">
                        <img
                          src={branding.preloader.customLogo}
                          alt="Current preloader logo"
                          className="preview-image"
                        />
                        <div className="file-actions">
                          <button
                            type="button"
                            onClick={() => preloaderLogoInputRef.current?.click()}
                            disabled={preloaderLogoUploading}
                            className="btn btn-outline"
                          >
                            {preloaderLogoUploading ? 'Uploading...' : 'Change Logo'}
                          </button>
                          <button
                            type="button"
                            onClick={handlePreloaderLogoReset}
                            disabled={preloaderLogoUploading}
                            className="btn btn-outline btn-danger"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="upload-placeholder">
                        <div className="upload-icon">📁</div>
                        <p>No custom preloader logo uploaded</p>
                        <button
                          type="button"
                          onClick={() => preloaderLogoInputRef.current?.click()}
                          disabled={preloaderLogoUploading}
                          className="btn btn-outline"
                        >
                          {preloaderLogoUploading ? 'Uploading...' : 'Upload Logo'}
                        </button>
                      </div>
                    )}
                    <input
                      ref={preloaderLogoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePreloaderLogoUpload}
                      style={{ display: 'none' }}
                    />
                    {errors['preloader.customLogo'] && (
                      <span className="error-text">{errors['preloader.customLogo']}</span>
                    )}
                    <small>Upload a custom logo specifically for the preloader (PNG, JPG, SVG recommended)</small>
                  </div>
                </div>

                <div className="form-group">
                  <label>Background Color (Optional)</label>
                  <div className="color-input">
                    <input
                      type="color"
                      value={branding.preloader?.backgroundColor || '#1c1e21'}
                      onChange={(e) => handlePreloaderInputChange('backgroundColor', e.target.value)}
                    />
                    <input
                      type="text"
                      value={branding.preloader?.backgroundColor || ''}
                      onChange={(e) => handlePreloaderInputChange('backgroundColor', e.target.value)}
                      placeholder="#1c1e21"
                      className={errors['preloader.backgroundColor'] ? 'error' : ''}
                    />
                  </div>
                  {errors['preloader.backgroundColor'] && (
                    <span className="error-text">{errors['preloader.backgroundColor']}</span>
                  )}
                  <small>Custom background color for the preloader</small>
                </div>

                {(branding.preloader?.type === 'default' || branding.preloader?.type === 'custom') && (
                  <div className="form-group">
                    <div className="toggle-group">
                      <label>Show "from" Text</label>
                      <Toggle
                        checked={branding.preloader?.showFromText !== false}
                        onChange={(enabled) => handlePreloaderInputChange('showFromText', enabled)}
                        label=""
                        disabled={loading}
                      />
                    </div>
                    <small>Display "from [Your App Name]" text below the logo</small>
                  </div>
                )}
              </>
            )}
          </div>

          {branding.preloader?.enabled !== false && (
            <div className="preloader-preview">
              <div className="preview-header">
                <h4>Preview</h4>

              </div>

              <div className="preview-container">
                <div className="preview-wrapper">
                  <PreloaderPreview branding={branding} />
                </div>
                <p className="preview-note">
                  This is how your preloader will appear to users
                </p>
              </div>
            </div>
          )}
        </div>
      </div> */}

      <div className="section">
        <h3>SEO</h3>

        <div className="form-group">
          <label>Site Title</label>
          <input
            type="text"
            value={(branding.seo && branding.seo.title) || ''}
            onChange={(e) => handleSeoChange('title', e.target.value)}
            placeholder="e.g., Your Brand | Plan. Collaborate. Grow."
          />
          <small>Used for the browser tab and link previews on your domain.</small>
        </div>

        <div className="form-group">
          <label>Meta Description</label>
          <textarea
            value={(branding.seo && branding.seo.description) || ''}
            onChange={(e) => handleSeoChange('description', e.target.value)}
            placeholder="Short summary for search engines and social cards"
            rows={3}
          />
        </div>

        <div className="form-group">
          <label>Meta Keywords/Tags</label>
          <input
            type="text"
            value={(branding.seo && (branding.seo.keywords || (Array.isArray(branding.seo.tags) ? branding.seo.tags.join(', ') : ''))) || ''}
            onChange={(e) => handleSeoChange('keywords', e.target.value)}
            placeholder="e.g., project management, kanban, collaboration"
          />
          <small>Comma-separated keywords. Optional.</small>
        </div>
      </div>

      {/* <div className="section">
        <h3>Contact & Support</h3>

        <div className="form-group">
          <label>Support Email (Optional)</label>
          <input
            type="email"
            value={branding.supportEmail || ''}
            onChange={(e) => handleInputChange('supportEmail', e.target.value)}
            placeholder="support@yourcompany.com"
            className={errors.supportEmail ? 'error' : ''}
          />
          {errors.supportEmail && <span className="error-text">{errors.supportEmail}</span>}
          <small>This email will be shown in the footer and help sections</small>
        </div>

        <div className="form-group">
          <label>Custom Footer Text (Optional)</label>
          <textarea
            value={branding.customFooter || ''}
            onChange={(e) => handleInputChange('customFooter', e.target.value)}
            placeholder="Additional footer information..."
            rows={3}
          />
          <small>Custom text to display in the footer</small>
        </div>
      </div>

      <div className="section">
        <h3>Legal & Policy Links</h3>

        <div className="form-group">
          <label>Custom Terms of Service URL (Optional)</label>
          <input
            type="url"
            value={branding.customTermsUrl || ''}
            onChange={(e) => handleInputChange('customTermsUrl', e.target.value)}
            placeholder="https://yourcompany.com/terms"
            className={errors.customTermsUrl ? 'error' : ''}
          />
          {errors.customTermsUrl && <span className="error-text">{errors.customTermsUrl}</span>}
          <small>Link to your custom terms of service page</small>
        </div>

        <div className="form-group">
          <label>Custom Privacy Policy URL (Optional)</label>
          <input
            type="url"
            value={branding.customPrivacyUrl || ''}
            onChange={(e) => handleInputChange('customPrivacyUrl', e.target.value)}
            placeholder="https://yourcompany.com/privacy"
            className={errors.customPrivacyUrl ? 'error' : ''}
          />
          {errors.customPrivacyUrl && <span className="error-text">{errors.customPrivacyUrl}</span>}
          <small>Link to your custom privacy policy page</small>
        </div>
      </div> */}

      {/* <div className="section">
        <h3>Advanced Customization</h3>

        <div className="form-group">
          <label>Custom CSS (Optional)</label>
          <textarea
            value={branding.customCSS || ''}
            onChange={(e) => handleInputChange('customCSS', e.target.value)}
            placeholder="
              .custom-button {
                background-color: var(--brand-color);
                border-radius: 8px;
              }"
            rows={8}
            className="code-textarea"
          />
          <small>Advanced: Add custom CSS to further customize the appearance</small>
        </div>
      </div> */}

      {/* <div className="section">
        <h3>Branding Options</h3>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={branding.hideZoobbeBranding || false}
              onChange={(e) => handleInputChange('hideZoobbeBranding', e.target.checked)}
            />
            <span className="checkmark"></span>
            Hide Zoobbe Branding
          </label>
          <small>Remove all Zoobbe references and make the platform completely yours</small>
        </div>
      </div> */}

      <div className="actions">
        <div className="actions-left">
          <button
            onClick={handleResetAll}
            disabled={loading || autoSaving}
            className="btn btn-outline btn-danger"
          >
            Reset All to Default
          </button>
        </div>
      </div>
      <AutoSaveStatus
        autoSaving={autoSaving}
        lastSaved={lastSaved}
      />
    </div>
  );
};

export default BrandingConfiguration;
